import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchMaintenancesMonth } from '../../features/maintenances/maintenancesSlice';
import CalendarGrid from './CalendarGrid';
import PageLoader from '../../components/PageLoader';
import CalendarFilterBuilder from '../../components/CalendarFilterBuilder';

import {
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import styles from './style.module.css';
import CustomTabs from '../../components/CustomTabs';
import CalendarList from './CalendarList';
import {
  setActiveCalendarView,
  selectActiveCalendarView,
} from '../../features/filters/filtersSlice';
import { INVALID_DATE_STRING } from '../../helpers';

function Calendar() {
  const [taskData, setTaskData] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [fetchedTasks, setFetchedTasks] = useState(null);
  const [filteredTasks, setFilteredTasks] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [matchingMachineIds, setMatchingMachineIds] = useState(null);
  const [showReportRepair, setShowReportRepair] = useState(true);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    if (
      startDate &&
      endDate &&
      startDate !== INVALID_DATE_STRING &&
      endDate !== INVALID_DATE_STRING
    ) {
      setIsLoading(true);
      setFetchedTasks([]);
      const startDateObj = new Date(startDate);
      startDateObj.setDate(startDateObj.getDate() - 1);
      const param = {
        startDate: startDateObj.toISOString().split('T')[0],
        endDate: endDate.split('T')[0],
      };
      const response = await dispatch(fetchMaintenancesMonth(param));
      if (response.payload) {
        setFetchedTasks(response.payload);
        setIsLoading(false);
      }
    }
  }, [startDate, endDate, dispatch]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, fetchData]);

  useEffect(() => {
    if (fetchedTasks && Array.isArray(fetchedTasks)) {
      const filtered = fetchedTasks.filter((task) => {
        if (matchingMachineIds !== null && !matchingMachineIds.includes(+task.machine?.id)) {
          return false;
        }
        if (!task.maintenance && !showReportRepair) {
          return false;
        }
        return true;
      });

      setFilteredTasks(filtered);
    }
  }, [fetchedTasks, matchingMachineIds, showReportRepair]);

  useEffect(() => {
    if (filteredTasks && Array.isArray(filteredTasks)) {
      const items = filteredTasks.map((item, index) => {
        const startDate = item.created_at ? item.created_at.split('T')[0] : '';

        let interval = '';
        if (item.maintenance?.interval) {
          try {
            interval = JSON.parse(item.maintenance.interval);
          } catch (error) {
            console.error('Error parsing interval for item', index, ':', error);
            interval = '';
          }
        }

        const description = item.maintenance?.description || '';

        return {
          id: index,
          machineId: item.machine?.id || '',
          machineName: item.machine?.name || '',
          machineNumber: item.machine?.number || '',
          groupName: item.machine?.machine_group?.name || '',
          groupImage: item.machine?.machine_group?.image || '',
          description: description,
          maintenanceName: item.maintenance ? item.maintenance.name : 'Report a repair',
          maintenanceType: item.maintenance
            ? item.maintenance.maintenance_type.name
            : 'Report a repair',
          start: startDate,
          status: item.status,
          filename: item.filename,
          original_filename: item.original_filename,
          comment: item.comment,
          maintanenceId: item.maintenance?.id,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          completedAt: item.completed_at,
          intervalType: interval.type,
          intervalValue: interval.value,
          notifyTime: item.maintenance?.notification_lead_time,
          responsibleFirstname: item.user?.firstname,
          responsibleLastname: item.user?.lastname,
          title: item.maintenance?.name || '',
        };
      });

      setTaskData(items);
    }
  }, [filteredTasks]);

  const value = useSelector(selectActiveCalendarView);

  return (
    <PageLoader
      loading={false}
      headerTitle={t('generic.calendar')}
      headerSubtitle={t('generic.calendarSubtitle')}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography variant="h5" fontWeight="bold">
            {t('generic.calendar')}
          </Typography>
          <Typography>{t('generic.calendarSubtitle')}</Typography>
        </div>
        {(isLoading || !startDate || !endDate) && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {!startDate || !endDate ? t('generic.initializingCalendar') || 'Initializing calendar...' : t('generic.loadingTasks')}
              </Typography>
            </div>
          </div>
        )}

        <CustomTabs
          tabs={[
            {
              key: 0,
              label: t('generic.calendar'),
              onClick: () => {
                dispatch(setActiveCalendarView(0));
              },
            },
            {
              key: 1,
              label: t('generic.list'),
              onClick: () => {
                dispatch(setActiveCalendarView(1));
              },
            },
          ]}
          activeTab={value}
        />

        <CalendarFilterBuilder onFilterChange={setMatchingMachineIds} />

        <FormControlLabel
          control={
            <Checkbox
              checked={showReportRepair}
              onChange={(e) => setShowReportRepair(e.target.checked)}
              size="small"
            />
          }
          label={t('generic.showRepairReports')}
          sx={{ mb: 1, ml: 0 }}
        />

        {value === 0 && (
          <CalendarGrid
            taskData={taskData}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            fetchData={fetchData}
          />
        )}
        {value === 1 && (
          <CalendarList
            taskData={taskData}
            setStartDate={setStartDate}
            startDate={startDate}
            setEndDate={setEndDate}
            endDate={endDate}
            fetchData={fetchData}
          />
        )}
      </div>
    </PageLoader>
  );
}

export default Calendar;
